/**
 * Sibling file: apollo/src/common/forms/commonValidations.ts
 * SPA is the source of truth for shared regexes and numeric constants.
 * When changing a shared rule (e.g. HOUSE_NUMBER_FORMAT, NAMES_REGEX),
 * update both sides.
 */
import * as Yup from 'yup';
import { AnyObject, Defined, ToggleDefault } from 'yup';
import { isAfter, isValid, parse } from 'date-fns';
import { FormatMessageFunction } from '@spa/lib/i18n/common/FormatMessageFunction';

export const ADDRESS_SUPPLEMENT_LENGTH: number = 100;
export const ALLOWED_UNICODE: RegExp = /^[\u0020-\u007F\u0080-\u00FF\u0100-\u017F\u0180-\u024F]*$/;
export const ALLOWED_UNICODE_WITH_LINE_BREAKS: RegExp =
  /^[\n\r\u0020-\u007F\u0080-\u00FF\u0100-\u017F\u0180-\u024F]*$/;
export const CITY_LENGTH: number = 50;
export const DHL_CITY_LENGTH: number = 40;
export const DHL_STREET_LENGTH: number = 50;
export const DHL_ADDRESS_SUPPLEMENT_LENGTH: number = 60;
export const DHL_HOUSE_NUMBER_LENGTH: number = 10;
export const DHL_HOUSE_NUMBER_LENGTH_AT: number = 20;
export const EMAIL_LENGTH: number = 64;
export const EMAIL_REGEX: RegExp =
  /^(?!.*\.\.)[A-Za-z0-9][A-Za-z0-9._%+-]*(?<!\.)@(?:[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?\.)+[A-Za-z]{2,}$/;
export const GIFT_CARD_ONLINE_ID_LENGTH: number = 16;
export const GIFT_CARD_ONLINE_ID_FORMAT: RegExp = /^[A-Za-z0-9]{16}$/;
export const GIFT_CARD_PIN_LENGTH: number = 4;
export const GIFT_CARD_PIN_DIGITS_FORMAT: RegExp = /^\d*$/;
export const HOUSE_NUMBER_FORMAT: RegExp =
  /^(?=[A-Za-z0-9])(?:[A-Za-z0-9\s-]+\/[A-Za-z0-9\s-]+(?:\/[A-Za-z0-9\s-]+)?|[A-Za-z0-9\s-]+)$/;
export const HOUSE_NUMBER_LENGTH: number = 10;
export const NAMES_LENGTH: number = 35;
export const NAMES_MIN_LENGTH: number = 2;
export const LASTNAMES_LENGTH: number = 50;
export const WISHLIST_NAME_LENGTH: number = 23;
export const NAMES_REGEX: RegExp = new RegExp(
  "^(?!.(.)\\1{3})(?!.--)(?!.'')\\p{L}[\\p{L}'\\-]",
  'u'
);
export const ORDER_NUMBER_FORMAT: RegExp = /^[A-Z0-9]{3,20}$/;
export const ORDER_NUMBER_LENGTH: number = 20;
export const PACKSTATION_NUMBER_LENGTH_AT: number = 4;
export const PACKSTATION_NUMBER_LENGTH_DE: number = 3;
export const PACKSTATION_PERSONAL_MAIL_NUMBER_PATTERN: RegExp = /^\d{6,10}$/;
export const PHONE_NUMBER_FORMAT: RegExp = /^\+?\d+$/;
export const PHONE_NUMBER_WITHOUT_COUNTRY_CODE_FORMAT: RegExp = /^\d+$/;
export const PHONE_NUMBER_MIN_LENGTH: number = 8;
export const PHONE_NUMBER_LENGTH: number = 16;
export const PWD_LOW_LETTERS: RegExp = /(?=.*[a-z])/;
export const PWD_MAX_LENGTH: number = 100;
export const PWD_MIN_LENGTH: number = 8;
export const PWD_MIN_MAX: RegExp = new RegExp(`.{${PWD_MIN_LENGTH},${PWD_MAX_LENGTH}}`);
export const PWD_NUMERIC: RegExp = /(?=.*\d)/;
export const PWD_SPECIAL: RegExp = /(?=.*[!@#$%^&*()\-_+.?"',/:;<=>`{}[\]\\~])/;
export const PWD_UPPER_LETTERS: RegExp = /(?=.*[A-Z])/;
export const STREET_LENGTH: number = 80;
export const TEXT_AREA_LENGTH: number = 5000;
export const ZIP_CODE_LENGTH_AT: number = 4;
export const ZIP_CODE_LENGTH_CH: number = 4;
export const ZIP_CODE_LENGTH_DE: number = 5;
const MIN_DATE = new Date(1900, 0, 1);

export const PWD_LOW_UPPER_LETTERS: RegExp = new RegExp(
  PWD_LOW_LETTERS.source + PWD_UPPER_LETTERS.source
);

export const PASSWORD_REGEX: RegExp = new RegExp(
  '^' +
    PWD_LOW_UPPER_LETTERS.source +
    PWD_NUMERIC.source +
    PWD_SPECIAL.source +
    PWD_MIN_MAX.source +
    '$'
);

export const PACKSTATION_WORDS: ReadonlyArray<string> = [
  'packstation',
  'pakstation',
  'packetstation',
  'paketstation',
  'paket station',
  'post station',
  'poststation'
];

export const zipCodesPatternPerCountry: Record<string, RegExp> = {
  DE: /^\d{5}$/,
  AT: /^\d{4}$/,
  CH: /^\d{4}$/
};

export const packstationNumberPatternPerCountry: Record<string, RegExp> = {
  DE: /^\d{3}$/,
  AT: /^\d{4}$/
};

const dateFormatFallback: string = 'dd.MM.yyyy';
const dateFormatPerCountry: Record<string, string> = {
  AT: 'dd.MM.yyyy',
  CH: 'dd.MM.yyyy',
  DE: 'dd.MM.yyyy'
};
export const getDateFormat = (countryCode: string): string =>
  dateFormatPerCountry[countryCode] ?? dateFormatFallback;

interface ValidationErrorMessages {
  readonly dateFuture: string;
  readonly dateInvalid: string;
  readonly defaultMax: string;
  readonly defaultMin: string;
  readonly emailMatches: string;
  readonly giftCardOnlineIdInvalid: string;
  readonly giftCardOnlineIdRequired: string;
  readonly giftCardPinDigitsOnly: string;
  readonly giftCardPinInvalid: string;
  readonly giftCardPinRequired: string;
  readonly houseNumberMatches: string;
  readonly invalidCharacters: string;
  readonly isMinor: string;
  readonly orderNumberMatches: string;
  readonly isPackstationMatches: string;
  readonly packstationNumberMatches: string;
  readonly personalMailNumberMatches: string;
  readonly passwordEqualsEmail: string;
  readonly passwordPolicy: string;
  readonly phoneNumberMatches: string;
  readonly phoneNumberMin: string;
  readonly phoneNumberWithoutCountryCodeMatches: string;
  readonly repeatEmail: string;
  readonly repeatNewPassword: string;
  readonly required: string;
  readonly requiredBirthDate: string;
  readonly zipCodeInvalid: string;
  readonly userNotLoggedIn: string;
}

const errorMessages: ValidationErrorMessages = {
  dateFuture: 'errors.date.future',
  dateInvalid: 'errors.date.invalid',
  emailMatches: 'errors.email.format',
  defaultMax: 'errors.defaultMax',
  defaultMin: 'errors.defaultMin',
  giftCardOnlineIdInvalid: 'errors.giftCard.onlineIdInvalid',
  giftCardOnlineIdRequired: 'errors.giftCard.onlineIdRequired',
  giftCardPinDigitsOnly: 'errors.giftCard.pinDigitsOnly',
  giftCardPinInvalid: 'errors.giftCard.pinInvalid',
  giftCardPinRequired: 'errors.giftCard.pinRequired',
  houseNumberMatches: 'errors.houseNumber.matches',
  invalidCharacters: 'errors.invalidCharacters',
  isMinor: 'errors.isMinor',
  orderNumberMatches: 'errors.orderNumber.matches',
  isPackstationMatches: 'errors.isPackstation.matches',
  packstationNumberMatches: 'errors.packstationNumber.matches',
  personalMailNumberMatches: 'errors.personalMailNumber.matches',
  passwordEqualsEmail: 'errors.password.equalsEmail',
  passwordPolicy: 'errors.password.policy',
  phoneNumberMatches: 'errors.phoneNumber.matches',
  phoneNumberMin: 'errors.phoneNumber.min',
  phoneNumberWithoutCountryCodeMatches: 'errors.phoneNumberWithoutCountryCode.matches',
  repeatEmail: 'errors.email.repeatEmail',
  repeatNewPassword: 'errors.password.repeatNewPassword',
  required: 'errors.required',
  requiredBirthDate: 'errors.requiredBirthDate',
  zipCodeInvalid: 'errors.zipCodeInvalid',
  userNotLoggedIn: 'errors.userNotLoggedIn'
};

const createIsValidDateTest =
  (countryCode: string) =>
  (value: string | undefined | null): boolean => {
    let result: boolean;

    if (!value) {
      result = true;
    } else {
      const parsedDate: Date = parse(value, getDateFormat(countryCode), new Date());
      result = isValid(parsedDate) && parsedDate >= MIN_DATE;
    }

    return result;
  };

const createIsNotFutureDateTest =
  (countryCode: string) =>
  (value: string | undefined | null): boolean =>
    !value || !isAfter(parse(value, getDateFormat(countryCode), new Date()), new Date());

export const createEmailSchema = (
  formatMessage: FormatMessageFunction,
  shouldTrim: boolean = false
): Yup.StringSchema<string> =>
  (shouldTrim ? Yup.string().trim() : Yup.string())
    .matches(EMAIL_REGEX, formatMessage(errorMessages.emailMatches))
    .max(EMAIL_LENGTH, formatMessage(errorMessages.defaultMax, { max: EMAIL_LENGTH }))
    .required(formatMessage(errorMessages.required));

export const createEnumSchema = <T extends { [key: string]: string | number }>(
  enumObj: T
): Yup.MixedSchema<T[keyof T]> => {
  return Yup.mixed<T[keyof T]>()
    .oneOf(Object.values(enumObj) as T[keyof T][])
    .required();
};

export const createGiftCardOnlineIdSchema = (
  formatMessage: FormatMessageFunction
): Yup.StringSchema<string> =>
  Yup.string()
    .transform((value: unknown): unknown =>
      typeof value === 'string' ? value.replace(/-/g, '') : value
    )
    .required(formatMessage(errorMessages.giftCardOnlineIdRequired))
    .test(
      'giftCardOnlineIdComplete',
      formatMessage(errorMessages.giftCardOnlineIdInvalid),
      (value: string | undefined): boolean =>
        !value || value.replace(/_/g, '').length >= GIFT_CARD_ONLINE_ID_LENGTH
    )
    .test(
      'giftCardOnlineIdChars',
      formatMessage(errorMessages.giftCardOnlineIdInvalid),
      (value: string | undefined): boolean => !value || GIFT_CARD_ONLINE_ID_FORMAT.test(value)
    );

export const createGiftCardPinSchema = (
  formatMessage: FormatMessageFunction
): Yup.StringSchema<string> =>
  Yup.string()
    .required(formatMessage(errorMessages.giftCardPinRequired))
    .test(
      'giftCardPinDigitsOnly',
      formatMessage(errorMessages.giftCardPinDigitsOnly),
      (value: string | undefined): boolean => !value || GIFT_CARD_PIN_DIGITS_FORMAT.test(value)
    )
    .test(
      'giftCardPinLength',
      formatMessage(errorMessages.giftCardPinInvalid),
      (value: string | undefined): boolean =>
        !value || !GIFT_CARD_PIN_DIGITS_FORMAT.test(value) || value.length >= GIFT_CARD_PIN_LENGTH
    );

export const createHouseNumberSchema = (
  formatMessage: FormatMessageFunction,
  customLength?: number
): Yup.StringSchema<string> => {
  return Yup.string()
    .matches(HOUSE_NUMBER_FORMAT, formatMessage(errorMessages.houseNumberMatches))
    .max(
      customLength ?? HOUSE_NUMBER_LENGTH,
      formatMessage(errorMessages.defaultMax, { max: customLength ?? HOUSE_NUMBER_LENGTH })
    )
    .required(formatMessage(errorMessages.required));
};

export const createNullableStringFieldSchema = (
  length: number,
  formatMessage: FormatMessageFunction
): Yup.StringSchema<
  Defined<string | undefined | null>,
  AnyObject,
  null,
  ToggleDefault<'', null>
> => {
  return Yup.string()
    .trim()
    .nullable()
    .default(null)
    .defined()
    .max(length, formatMessage(errorMessages.defaultMax, { max: length }))
    .test('onlyAllowedUnicode', formatMessage(errorMessages.invalidCharacters), function (value) {
      return !value || ALLOWED_UNICODE.test(value);
    });
};

export const createOptionalFutureDateSchema = (
  countryCode: string,
  formatMessage: FormatMessageFunction
): Yup.StringSchema =>
  Yup.string()
    .test(
      'isValidDate',
      formatMessage(errorMessages.dateInvalid),
      createIsValidDateTest(countryCode)
    )
    .test(
      'isNotFutureDate',
      formatMessage(errorMessages.dateInvalid),
      (value: string | undefined): boolean =>
        !value || isAfter(parse(value, getDateFormat(countryCode), new Date()), new Date())
    );

export const createOptionalHouseNumberSchema = (
  formatMessage: FormatMessageFunction,
  customLength?: number
): Yup.StringSchema =>
  Yup.string()
    .transform((value) => (value === '' ? undefined : value))
    .matches(HOUSE_NUMBER_FORMAT, formatMessage(errorMessages.houseNumberMatches))
    .max(
      customLength ?? HOUSE_NUMBER_LENGTH,
      formatMessage(errorMessages.defaultMax, { max: customLength ?? HOUSE_NUMBER_LENGTH })
    );

export const createOptionalPastDateSchema = (
  countryCode: string,
  formatMessage: FormatMessageFunction
): Yup.StringSchema =>
  Yup.string()
    .test(
      'isValidDate',
      formatMessage(errorMessages.dateInvalid),
      createIsValidDateTest(countryCode)
    )
    .test(
      'isPastDate',
      formatMessage(errorMessages.dateFuture),
      (value: string | undefined): boolean =>
        !value || !isAfter(parse(value, getDateFormat(countryCode), new Date()), new Date())
    );

export const createOptionalPhoneNumberSchema = (
  formatMessage: FormatMessageFunction
): Yup.StringSchema<Yup.Maybe<string>> =>
  Yup.string()
    .nullable()
    .notRequired()
    .transform((value) => (value === '' ? undefined : value))
    .min(
      PHONE_NUMBER_MIN_LENGTH,
      formatMessage(errorMessages.phoneNumberMin, {
        min: PHONE_NUMBER_MIN_LENGTH
      })
    )
    .max(PHONE_NUMBER_LENGTH, formatMessage(errorMessages.defaultMax, { max: PHONE_NUMBER_LENGTH }))
    .matches(PHONE_NUMBER_FORMAT, formatMessage(errorMessages.phoneNumberMatches));

export const createOptionalAdultPastDateSchema = (
  countryCode: string,
  formatMessage: FormatMessageFunction,
  minAge: number = 18
): Yup.StringSchema<Yup.Maybe<string>> =>
  Yup.string()
    .notRequired()
    .nullable()
    .test(
      'isValidDate',
      formatMessage(errorMessages.dateInvalid),
      createIsValidDateTest(countryCode)
    )
    .test(
      'isNotFutureDate',
      formatMessage(errorMessages.dateFuture),
      createIsNotFutureDateTest(countryCode)
    )
    .test(
      'isAdult',
      formatMessage(errorMessages.isMinor, { age: minAge }),
      (value: string | undefined | null): boolean => {
        let result: boolean;
        if (value) {
          const parsed = parse(value, getDateFormat(countryCode), new Date());
          const now = new Date();
          const cutoff = new Date(now.getFullYear() - minAge, now.getMonth(), now.getDate());
          result = parsed <= cutoff;
        } else {
          result = true;
        }
        return result;
      }
    );

export const createAdultPastDateSchema = (
  countryCode: string,
  formatMessage: FormatMessageFunction,
  minAge: number = 18
): Yup.StringSchema<string> =>
  Yup.string()
    .required(formatMessage(errorMessages.requiredBirthDate))
    .test(
      'isValidDate',
      formatMessage(errorMessages.dateInvalid),
      createIsValidDateTest(countryCode)
    )
    .test(
      'isNotFutureDate',
      formatMessage(errorMessages.dateFuture),
      createIsNotFutureDateTest(countryCode)
    )
    .test(
      'isAdult',
      formatMessage(errorMessages.isMinor, { age: minAge }),
      (value: string | undefined | null): boolean => {
        let result: boolean;
        if (value) {
          const parsed = parse(value, getDateFormat(countryCode), new Date());
          const now = new Date();
          const cutoff = new Date(now.getFullYear() - minAge, now.getMonth(), now.getDate());
          result = parsed <= cutoff;
        } else {
          result = true;
        }
        return result;
      }
    );

export const createOptionalStringFieldSchema = (
  length: number,
  formatMessage: FormatMessageFunction,
  allowLB: boolean = false
): Yup.StringSchema =>
  Yup.string()
    .trim()
    .max(length, formatMessage(errorMessages.defaultMax, { max: length }))
    .test('onlyAllowedUnicode', formatMessage(errorMessages.invalidCharacters), function (value) {
      const pattern: RegExp = allowLB ? ALLOWED_UNICODE_WITH_LINE_BREAKS : ALLOWED_UNICODE;
      return !value || pattern.test(value);
    });

export const createOptionalOrderNumberSchema = (
  formatMessage: FormatMessageFunction
): Yup.StringSchema =>
  Yup.string()
    .transform((value) => (value === '' ? undefined : value))
    .matches(ORDER_NUMBER_FORMAT, formatMessage(errorMessages.orderNumberMatches))
    .max(
      ORDER_NUMBER_LENGTH,
      formatMessage(errorMessages.defaultMax, { max: ORDER_NUMBER_LENGTH })
    );

export const createOptionalZipCodeSchema = (
  countryCode: string,
  formatMessage: FormatMessageFunction
): Yup.StringSchema =>
  Yup.string().test(
    'zipCodePattern',
    formatMessage(errorMessages.zipCodeInvalid),
    function (value) {
      const pattern: RegExp = zipCodesPatternPerCountry[countryCode];
      return !value || !pattern || pattern.test(value);
    }
  );

export const createOrderNumberSchema = (
  formatMessage: FormatMessageFunction
): Yup.StringSchema<string> =>
  Yup.string()
    .trim()
    .matches(ORDER_NUMBER_FORMAT, formatMessage(errorMessages.orderNumberMatches))
    .max(ORDER_NUMBER_LENGTH, formatMessage(errorMessages.defaultMax, { max: ORDER_NUMBER_LENGTH }))
    .required(formatMessage(errorMessages.required));

export const createPackStationNumberSchema = (
  countryCode: string,
  formatMessage: FormatMessageFunction
): Yup.StringSchema<string> =>
  Yup.string()
    .required(formatMessage(errorMessages.required))
    .test(
      'packstationNumberPattern',
      formatMessage(errorMessages.packstationNumberMatches, {
        min: getPackstationNumberLengthPerCountry(countryCode)
      }),
      function (value) {
        const pattern: RegExp = packstationNumberPatternPerCountry[countryCode];
        return !value || !pattern || pattern.test(value);
      }
    );

export const isPasswordEqualToUsername = (
  password: string | undefined,
  username: string | undefined
): boolean =>
  password !== undefined &&
  username !== undefined &&
  password.toLowerCase() === username.trim().toLowerCase();

export interface PasswordSchemaOptions {
  readonly requiredMessage?: string;
  readonly username?: string;
}

export const createPasswordSchema = (
  formatMessage: FormatMessageFunction,
  { requiredMessage, username }: PasswordSchemaOptions = {}
): Yup.StringSchema<string> =>
  Yup.string()
    .required(requiredMessage ?? formatMessage(errorMessages.required))
    .matches(PASSWORD_REGEX, formatMessage(errorMessages.passwordPolicy))
    .test(
      'passwordNotEqualToUsername',
      formatMessage(errorMessages.passwordEqualsEmail),
      (value: string | undefined): boolean => !isPasswordEqualToUsername(value, username)
    );

export const createPersonalMailNumberSchema = (
  formatMessage: FormatMessageFunction
): Yup.StringSchema<string> =>
  Yup.string()
    .matches(
      PACKSTATION_PERSONAL_MAIL_NUMBER_PATTERN,
      formatMessage(errorMessages.personalMailNumberMatches)
    )
    .required(formatMessage(errorMessages.required));

export const createPhoneNumberSchema = (
  formatMessage: FormatMessageFunction
): Yup.StringSchema<string> =>
  Yup.string()
    .matches(PHONE_NUMBER_FORMAT, formatMessage(errorMessages.phoneNumberMatches))
    .min(
      PHONE_NUMBER_MIN_LENGTH,
      formatMessage(errorMessages.phoneNumberMin, {
        min: PHONE_NUMBER_MIN_LENGTH
      })
    )
    .max(PHONE_NUMBER_LENGTH, formatMessage(errorMessages.defaultMax, { max: PHONE_NUMBER_LENGTH }))
    .required(formatMessage(errorMessages.required));

export const createPhoneNumberWithoutCountryCodeSchema = (
  formatMessage: FormatMessageFunction
): Yup.StringSchema<string | undefined> =>
  Yup.string()
    .min(
      PHONE_NUMBER_MIN_LENGTH,
      formatMessage(errorMessages.phoneNumberMin, {
        min: PHONE_NUMBER_MIN_LENGTH
      })
    )
    .matches(
      PHONE_NUMBER_WITHOUT_COUNTRY_CODE_FORMAT,
      formatMessage(errorMessages.phoneNumberWithoutCountryCodeMatches)
    )
    .max(
      PHONE_NUMBER_LENGTH,
      formatMessage(errorMessages.defaultMax, { max: PHONE_NUMBER_LENGTH })
    );

export const createRepeatEmailSchema = (
  originalEmail: string,
  formatMessage: FormatMessageFunction
): Yup.StringSchema =>
  Yup.string().test('validate-repeat-email', errorMessages.required, function (value) {
    const { email } = this.parent;
    let errorMessage: string | undefined;

    if (email !== originalEmail) {
      if (!value) {
        errorMessage = formatMessage(errorMessages.required);
      } else if (!Yup.string().email().isValidSync(value)) {
        errorMessage = formatMessage(errorMessages.emailMatches);
      } else if (value !== email) {
        errorMessage = formatMessage(errorMessages.repeatEmail);
      }
    }

    return errorMessage ? this.createError({ message: errorMessage }) : true;
  });

export const createRepeatPasswordSchema = (
  formatMessage: FormatMessageFunction
): Yup.StringSchema<string> =>
  Yup.string()
    .oneOf([Yup.ref('newPassword')], formatMessage(errorMessages.repeatNewPassword))
    .required(formatMessage(errorMessages.required));

export const createRequiredBooleanSchema = (
  formatMessage: FormatMessageFunction
): Yup.BooleanSchema<boolean> =>
  Yup.boolean()
    .oneOf([true], formatMessage(errorMessages.required))
    .required(formatMessage(errorMessages.required));

export const createRequiredPastDateSchema = (
  countryCode: string,
  formatMessage: FormatMessageFunction
): Yup.StringSchema =>
  Yup.string()
    .required(formatMessage(errorMessages.required))
    .test(
      'isValidDate',
      formatMessage(errorMessages.dateInvalid),
      createIsValidDateTest(countryCode)
    )
    .test(
      'isPastDate',
      formatMessage(errorMessages.dateFuture),
      (value: string | undefined): boolean =>
        !value || !isAfter(parse(value, getDateFormat(countryCode), new Date()), new Date())
    );

export const createRequiredStringSchema = (
  formatMessage: FormatMessageFunction
): Yup.StringSchema<string> => Yup.string().required(formatMessage(errorMessages.required));

export const createStringFieldSchema = (
  length: number,
  formatMessage: FormatMessageFunction,
  allowLB: boolean = false
): Yup.StringSchema<string> =>
  Yup.string()
    .trim()
    .max(length, formatMessage(errorMessages.defaultMax, { max: length }))
    .required(formatMessage(errorMessages.required))
    .test('onlyAllowedUnicode', formatMessage(errorMessages.invalidCharacters), function (value) {
      const pattern: RegExp = allowLB ? ALLOWED_UNICODE_WITH_LINE_BREAKS : ALLOWED_UNICODE;
      return !value || pattern.test(value);
    });

export const createStringStreetNotAllowingPackstationFieldSchema = (
  length: number,
  formatMessage: FormatMessageFunction,
  allowLB: boolean = false
): Yup.StringSchema<string> =>
  createStringFieldSchema(length, formatMessage, allowLB).test(
    'isPackstation',
    formatMessage(errorMessages.isPackstationMatches),
    function (value: string): boolean {
      const lowerCaseValue: string = value.toLowerCase();
      return !PACKSTATION_WORDS.some((word: string): boolean => lowerCaseValue.includes(word));
    }
  );

export const createZipCodeSchema = (
  countryCode: string,
  formatMessage: FormatMessageFunction
): Yup.StringSchema<string> =>
  Yup.string()
    .required(formatMessage(errorMessages.required))
    .test('zipCodePattern', formatMessage(errorMessages.zipCodeInvalid), function (value) {
      const pattern: RegExp = zipCodesPatternPerCountry[countryCode];
      return !value || !pattern || pattern.test(value);
    });

export const getPackstationNumberLengthPerCountry = (country: string): number => {
  let result: number;
  switch (country) {
    case 'AT':
      result = PACKSTATION_NUMBER_LENGTH_AT;
      break;
    case 'DE':
    default:
      result = PACKSTATION_NUMBER_LENGTH_DE;
      break;
  }
  return result;
};

export const getHouseNumberLengthPerCountry = (country: string): number => {
  switch (country) {
    case 'AT':
      return DHL_HOUSE_NUMBER_LENGTH_AT;
    default:
      return DHL_HOUSE_NUMBER_LENGTH;
  }
};

export const getZipNumberLengthPerCountry = (country: string): number => {
  let result: number;
  switch (country) {
    case 'AT':
      result = ZIP_CODE_LENGTH_AT;
      break;
    case 'CH':
      result = ZIP_CODE_LENGTH_CH;
      break;
    case 'DE':
    default:
      result = ZIP_CODE_LENGTH_DE;
      break;
  }
  return result;
};

// Shared validation chain for name fields. Tolerant of empty values (min/regex/unicode
// checks are skipped when blank); the `required` constraint is layered on by the wrappers
// below so the optional and non-optional variants stay in sync.
const buildNameFieldSchema = (
  formatMessage: FormatMessageFunction,
  customLength?: number
): Yup.StringSchema =>
  Yup.string()
    .trim()
    .matches(NAMES_REGEX, {
      message: formatMessage(errorMessages.invalidCharacters),
      excludeEmptyString: true
    })
    .max(
      customLength ?? NAMES_LENGTH,
      formatMessage(errorMessages.defaultMax, { max: customLength ?? NAMES_LENGTH })
    )
    .test('minLength', formatMessage(errorMessages.defaultMin), function (value) {
      return !value || value.length >= NAMES_MIN_LENGTH;
    })
    .test('onlyAllowedUnicode', formatMessage(errorMessages.invalidCharacters), function (value) {
      return !value || ALLOWED_UNICODE.test(value);
    });

export const createFirstNameFieldSchema = (
  formatMessage: FormatMessageFunction,
  customLength?: number
): Yup.StringSchema<string> =>
  Yup.string()
    .trim()
    .min(NAMES_MIN_LENGTH, formatMessage(errorMessages.defaultMin))
    .matches(NAMES_REGEX, formatMessage(errorMessages.invalidCharacters))
    .max(
      customLength ?? NAMES_LENGTH,
      formatMessage(errorMessages.defaultMax, { max: customLength ?? NAMES_LENGTH })
    )
    .required(formatMessage(errorMessages.required))
    .test('onlyAllowedUnicode', formatMessage(errorMessages.invalidCharacters), function (value) {
      return !value || ALLOWED_UNICODE.test(value);
    });

export const createOptionalFirstNameFieldSchema = (
  formatMessage: FormatMessageFunction,
  customLength?: number
): Yup.StringSchema => buildNameFieldSchema(formatMessage, customLength);

export const createUserIdSchema = (
  formatMessage: FormatMessageFunction
): Yup.StringSchema<string> => Yup.string().required(formatMessage(errorMessages.userNotLoggedIn));

export const createLastNameFieldSchema = (
  formatMessage: FormatMessageFunction
): Yup.StringSchema<string> => createFirstNameFieldSchema(formatMessage, LASTNAMES_LENGTH);

export const createOptionalLastNameFieldSchema = (
  formatMessage: FormatMessageFunction
): Yup.StringSchema => createOptionalFirstNameFieldSchema(formatMessage, LASTNAMES_LENGTH);
